import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import Layout from './components/Layout.jsx';
import { useAuth } from './lib/AuthContext.jsx';
import { Empty, Loading } from './components/ui.jsx';

import Login from './pages/Login.jsx';
import Register from './pages/Register.jsx';
import RegisterPatient from './pages/RegisterPatient.jsx';
import RegisterNurse from './pages/RegisterNurse.jsx';
import ForgotPassword from './pages/ForgotPassword.jsx';
import ResetPassword from './pages/ResetPassword.jsx';

import PatientHome from './pages/patient/Home.jsx';
import Search from './pages/patient/Search.jsx';
import NurseProfilePublic from './pages/patient/NurseProfile.jsx';
import BookNurse from './pages/patient/Book.jsx';
import MyBookings from './pages/patient/Bookings.jsx';
import BookingDetail from './pages/patient/BookingDetail.jsx';
import PatientProfile from './pages/patient/Profile.jsx';

import NurseDashboard from './pages/nurse/Dashboard.jsx';
import NurseBookings from './pages/nurse/Bookings.jsx';
import NurseAvailability from './pages/nurse/Availability.jsx';
import NurseEarnings from './pages/nurse/Earnings.jsx';
import NurseProfileEdit from './pages/nurse/Profile.jsx';

import AdminDashboard from './pages/admin/Dashboard.jsx';
import AdminBook from './pages/admin/Book.jsx';
import AdminVerifications from './pages/admin/Verifications.jsx';
import AdminUsers from './pages/admin/Users.jsx';
import AdminBookings from './pages/admin/Bookings.jsx';
import AdminComplaints from './pages/admin/Complaints.jsx';
import AdminServices from './pages/admin/Services.jsx';

const HOME_FOR = { patient: '/', nurse: '/nurse', admin: '/admin' };

function Protected({ roles, children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <Loading rows={3} />;
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={HOME_FOR[user.role] || '/'} replace />;
  return children;
}

export default function App() {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Auth screens render without the app chrome.
  const AUTH_ROUTES = [
    '/login',
    '/register',
    '/register/patient',
    '/register/nurse',
    '/forgot-password',
    '/reset-password',
  ];
  if (AUTH_ROUTES.includes(location.pathname)) {
    if (loading) return null;
    // A signed-in user has no business on these screens — except mid-reset,
    // where ResetPassword signs them in itself and then redirects.
    if (user && location.pathname !== '/reset-password') {
      return <Navigate to={HOME_FOR[user.role] || '/'} replace />;
    }
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/register/patient" element={<RegisterPatient />} />
        <Route path="/register/nurse" element={<RegisterNurse />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
      </Routes>
    );
  }

  return (
    <Layout>
      <Routes>
        {/* Patient / public */}
        <Route path="/" element={user?.role === 'nurse' ? <Navigate to="/nurse" replace /> : user?.role === 'admin' ? <Navigate to="/admin" replace /> : <PatientHome />} />
        <Route path="/search" element={<Search />} />
        <Route path="/nurses/:id" element={<NurseProfilePublic />} />
        <Route
          path="/book/:id"
          element={
            <Protected roles={['patient']}>
              <BookNurse />
            </Protected>
          }
        />
        <Route
          path="/bookings"
          element={
            <Protected roles={['patient']}>
              <MyBookings />
            </Protected>
          }
        />
        <Route
          path="/bookings/:id"
          element={
            <Protected roles={['patient', 'nurse', 'admin']}>
              <BookingDetail />
            </Protected>
          }
        />
        <Route
          path="/profile"
          element={
            <Protected>
              <PatientProfile />
            </Protected>
          }
        />

        {/* Nurse */}
        <Route path="/nurse" element={<Protected roles={['nurse']}><NurseDashboard /></Protected>} />
        <Route path="/nurse/bookings" element={<Protected roles={['nurse']}><NurseBookings /></Protected>} />
        <Route path="/nurse/bookings/:id" element={<Protected roles={['nurse']}><BookingDetail /></Protected>} />
        <Route path="/nurse/availability" element={<Protected roles={['nurse']}><NurseAvailability /></Protected>} />
        <Route path="/nurse/earnings" element={<Protected roles={['nurse']}><NurseEarnings /></Protected>} />
        <Route path="/nurse/profile" element={<Protected roles={['nurse']}><NurseProfileEdit /></Protected>} />
        <Route path="/nurse/reviews" element={<Protected roles={['nurse']}><NurseProfileEdit /></Protected>} />

        {/* Admin */}
        <Route path="/admin" element={<Protected roles={['admin']}><AdminDashboard /></Protected>} />
        <Route path="/admin/book/:id" element={<Protected roles={['admin']}><AdminBook /></Protected>} />
        <Route path="/admin/verifications" element={<Protected roles={['admin']}><AdminVerifications /></Protected>} />
        <Route path="/admin/users" element={<Protected roles={['admin']}><AdminUsers /></Protected>} />
        <Route path="/admin/bookings" element={<Protected roles={['admin']}><AdminBookings /></Protected>} />
        <Route path="/admin/complaints" element={<Protected roles={['admin']}><AdminComplaints /></Protected>} />
        <Route path="/admin/services" element={<Protected roles={['admin']}><AdminServices /></Protected>} />

        <Route
          path="*"
          element={
            <Empty
              art="not-found"
              title="Page not found"
              action={
                <Link to="/" className="btn btn-primary">
                  Go to home
                </Link>
              }
            >
              The page you were looking for does not exist or has moved.
            </Empty>
          }
        />
      </Routes>
    </Layout>
  );
}
